import { useState, useCallback, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, isUpgradeRequiredError } from "@/lib/queryClient";
import { InlineUpgradePanel } from "@/components/inline-upgrade-panel";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link, useParams } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, ExternalLink, FileText, CheckCircle, XCircle, Clock, AlertTriangle,
  TrendingUp, DollarSign, Home, Bed, Maximize, ChevronUp, ChevronDown, Download, Search, RefreshCw, Settings2,
  Pencil, X as XIcon, GitCompareArrows, ArrowUp, ArrowDown, Minus, Star
} from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { BuyBoxSubnav } from "@/components/buy-box/buy-box-subnav";
import { BuyBoxAgentTags } from "@/components/buy-box/buy-box-agent-tags";
import type { BuyBoxAgentTag } from "@shared/schema";

type Report = {
  id: number;
  city: string;
  state: string;
  status: string;
  statusNote?: string | null;
  queuePosition?: number | null;
  queueEstimatedWaitSeconds?: number | null;
  totalFound: number;
  totalPassed: number;
  totalFailed: number;
  startedAt: string;
  completedAt?: string | null;
  criteriaSnapshot?: Record<string, unknown> | null;
  defaultsSnapshot?: Record<string, unknown> | null;
  weeklyRate?: string | number | null;
  // Buy Box Agents — recorded at run time on daily agent reports.
  agentId?: number | null;
  agentNumber?: number | null;
  versionId?: number | null;
  versionNumber?: number | null;
  versionLabel?: string | null;
  agentRunDate?: string | null;
  lookbackDays?: number | null;
  runName?: string | null;
};

/** The "found by" tag every property in an agent-run report carries. */
export function reportAgentTag(report: Report): BuyBoxAgentTag | null {
  if (!report.agentRunDate) return null;
  return {
    agentId: report.agentId ?? null,
    agentNumber: report.agentNumber ?? null,
    versionId: report.versionId ?? null,
    versionNumber: report.versionNumber ?? null,
    versionLabel: report.versionLabel ?? null,
    city: report.city,
    state: report.state,
    runDate: report.agentRunDate,
    reportId: report.id,
  };
}

function AgentRunBadges({ report, testId }: { report: Report; testId?: string }) {
  if (!report.agentRunDate) return null;
  const badge = "inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700";
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={testId}>
      <span className={badge}>Agent {report.agentNumber ?? "?"}</span>
      <span className={badge}>Buy Box V{report.versionNumber ?? "?"}{report.versionLabel ? ` (${report.versionLabel})` : ""}</span>
      <span className={badge}>{report.city}, {report.state}</span>
      <span className={badge}>{report.agentRunDate}</span>
      {report.lookbackDays ? <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">New in last {report.lookbackDays === 1 ? "day" : `${report.lookbackDays} days`}</span> : null}
    </div>
  );
}

type Result = {
  id: number;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  price?: string | null;
  sqft?: number | null;
  originalBedrooms?: number | null;
  originalBathrooms?: string | null;
  convertedBedrooms?: number | null;
  convertedBathrooms?: number | null;
  imageUrl?: string | null;
  propertyType?: string | null;
  monthlyCashFlow?: string | null;
  cocReturn?: string | null;
  totalCashInvested?: string | null;
  totalMonthlyIncome?: string | null;
  totalMonthlyExpenses?: string | null;
  mortgagePI?: string | null;
  annualTax?: string | null;
  annualTaxIsEstimate?: boolean | null;
  monthlyHoa?: string | null;
  isCornerLot?: boolean | null;
  garageSpaces?: number | null;
  lotSize?: number | null;
  description?: string | null;
  passesCocReturn?: boolean | null;
  passesCashFlow?: boolean | null;
  passesPurchasePrice?: boolean | null;
  passesBedrooms?: boolean | null;
  passesSqft?: boolean | null;
  passesLotSize?: boolean | null;
  passesAll?: boolean | null;
  listingUrl?: string | null;
  listingId?: string | null;
  errorMessage?: string | null;
};

const STUCK_SCAN_STATUS_NOTE =
  "Server restarted before this scan could finish. Please run a new scan.";

function isStuckRestart(report: { status: string; statusNote?: string | null }) {
  return report.status === "failed" && report.statusNote === STUCK_SCAN_STATUS_NOTE;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "queued") return <Badge className="bg-gray-100 text-gray-700 border-gray-200" data-testid="badge-status-queued"><Clock className="h-3 w-3 mr-1" />Queued</Badge>;
  if (status === "running") return <Badge className="bg-blue-100 text-blue-700 border-blue-200"><Clock className="h-3 w-3 mr-1" />Running</Badge>;
  if (status === "complete") return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Complete</Badge>;
  if (status === "partially_complete") return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" />Partial</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
}

function isInProgress(status: string) {
  return status === "running" || status === "queued";
}

function fmtCurrency(v: string | number | null | undefined) {
  const n = parseFloat(String(v || "0"));
  if (isNaN(n)) return "—";
  return n < 0
    ? `-$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatWait(seconds: number): string {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

function fmtPct(v: string | number | null | undefined) {
  const n = parseFloat(String(v || "0"));
  if (isNaN(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function PassBadge({ passes, label }: { passes: boolean | null | undefined; label: string }) {
  if (passes === null || passes === undefined) return null;
  return passes
    ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 font-medium"><CheckCircle className="h-2.5 w-2.5" />{label}</span>
    : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-medium"><XCircle className="h-2.5 w-2.5" />{label}</span>;
}

function ReportsList() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const reportsQuery = useQuery<Report[]>({
    queryKey: ["/api/app/buy-box/reports"],
    refetchInterval: (query) => {
      const data = query.state.data as Report[] | undefined;
      if (data && data.some(r => isInProgress(r.status))) return 5000;
      return false;
    },
  });
  const reports = reportsQuery.data || [];

  if (isUpgradeRequiredError(reportsQuery.error)) {
    return <InlineUpgradePanel error={reportsQuery.error} />;
  }

  if (reportsQuery.isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium text-gray-500">No Buy Box reports yet</p>
        <p className="text-sm mt-1">Go to a city in Research and click "Find Buy Box properties"</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="reports-list" data-tour="buy-box-reports-chart">
      {reports.map(report => (
        <div key={report.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`report-card-${report.id}`}>
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Home className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate" data-testid={`text-report-name-${report.id}`}>{report.runName || `${report.city}, ${report.state}`}</div>
              <div className="text-xs text-gray-400">{new Date(report.startedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
              <AgentRunBadges report={report} testId={`badges-report-${report.id}`} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center hidden sm:block">
              <div className="text-lg font-bold text-gray-900">{report.totalPassed}</div>
              <div className="text-xs text-gray-400">Passed</div>
            </div>
            <div className="text-center hidden sm:block">
              <div className="text-lg font-bold text-gray-600">{report.totalFound}</div>
              <div className="text-xs text-gray-400">Found</div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <StatusBadge status={report.status} />
            </div>
            <Link href={`/app/buy-box/reports/${report.id}`}>
              <Button size="sm" variant="outline" data-testid={`button-view-report-${report.id}`}>
                {isInProgress(report.status) ? "View Live" : "View Report"}
              </Button>
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

type SortKey = "cocReturn" | "monthlyCashFlow" | "price" | "convertedBedrooms";

type CriteriaFields = {
  minCocReturn: string;
  minMonthlyCashFlow: string;
  maxPurchasePrice: string;
  minBedrooms: string;
  maxBedrooms: string;
  minSqft: string;
  maxSqft: string;
};

const emptyCriteria: CriteriaFields = { minCocReturn: "", minMonthlyCashFlow: "", maxPurchasePrice: "", minBedrooms: "", maxBedrooms: "", minSqft: "", maxSqft: "" };

function parseCriteriaSnapshot(snap: Record<string, unknown> | null | undefined): CriteriaFields {
  if (!snap) return { ...emptyCriteria };
  return {
    minCocReturn: snap.minCocReturn != null ? String(snap.minCocReturn) : "",
    minMonthlyCashFlow: snap.minMonthlyCashFlow != null ? String(snap.minMonthlyCashFlow) : "",
    maxPurchasePrice: snap.maxPurchasePrice != null ? String(snap.maxPurchasePrice) : "",
    minBedrooms: snap.minBedrooms != null ? String(snap.minBedrooms) : "",
    maxBedrooms: snap.maxBedrooms != null ? String(snap.maxBedrooms) : "",
    minSqft: snap.minSqft != null ? String(snap.minSqft) : "",
    maxSqft: snap.maxSqft != null ? String(snap.maxSqft) : "",
  };
}

function criteriaToPayload(fields: CriteriaFields): Record<string, number | null> {
  const toNum = (v: string) => v === "" ? null : parseFloat(v);
  return {
    minCocReturn: toNum(fields.minCocReturn),
    minMonthlyCashFlow: toNum(fields.minMonthlyCashFlow),
    maxPurchasePrice: toNum(fields.maxPurchasePrice),
    minBedrooms: toNum(fields.minBedrooms),
    maxBedrooms: toNum(fields.maxBedrooms),
    minSqft: toNum(fields.minSqft),
    maxSqft: toNum(fields.maxSqft),
  };
}

function formatWeeklyRate(rate: string | number | null | undefined): { value: string; isFallback: boolean } {
  if (rate == null || rate === "") return { value: "default $200/wk", isFallback: true };
  const num = typeof rate === "number" ? rate : parseFloat(rate);
  if (!isFinite(num) || num <= 0) return { value: "default $200/wk", isFallback: true };
  return { value: `$${num.toFixed(0)}/wk`, isFallback: false };
}

function DiffArrow({ a, b, higherIsBetter = true }: { a: number; b: number; higherIsBetter?: boolean }) {
  if (a === b || (!isFinite(a) && !isFinite(b))) {
    return <span className="inline-flex items-center text-gray-400 text-[10px]"><Minus className="h-3 w-3" /></span>;
  }
  const better = higherIsBetter ? b > a : b < a;
  const diff = b - a;
  const Icon = b > a ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${better ? "text-green-600" : "text-red-600"}`}>
      <Icon className="h-3 w-3" />{diff > 0 ? "+" : ""}{diff.toLocaleString("en-US", { maximumFractionDigits: 1 })}
    </span>
  );
}

function ComparisonView({
  reportA,
  resultsA,
  reportB,
  resultsB,
  onClear,
}: {
  reportA: Report;
  resultsA: Result[];
  reportB: Report;
  resultsB: Result[];
  onClear: () => void;
}) {
  const rateA = parseFloat(String(reportA.weeklyRate ?? "0")) || 0;
  const rateB = parseFloat(String(reportB.weeklyRate ?? "0")) || 0;

  const passingA = resultsA.filter(r => r.passesAll);
  const passingB = resultsB.filter(r => r.passesAll);

  const avgCoc = (rs: Result[]) => {
    const vals = rs.map(r => parseFloat(String(r.cocReturn || "0"))).filter(n => isFinite(n));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  };
  const avgCocA = avgCoc(passingA);
  const avgCocB = avgCoc(passingB);

  const keyOf = (r: Result) => r.listingId || r.address || String(r.id);
  const mapA = new Map<string, Result>();
  const mapB = new Map<string, Result>();
  passingA.forEach(r => mapA.set(keyOf(r), r));
  passingB.forEach(r => mapB.set(keyOf(r), r));
  const keySet = new Set<string>();
  Array.from(mapA.keys()).forEach(k => keySet.add(k));
  Array.from(mapB.keys()).forEach(k => keySet.add(k));
  const allKeys = Array.from(keySet);
  allKeys.sort((k1, k2) => {
    const a = mapA.get(k1) || mapB.get(k1)!;
    const b = mapA.get(k2) || mapB.get(k2)!;
    return (a.address || "").localeCompare(b.address || "");
  });

  const HeaderCol = ({ report, label }: { report: Report; label: string }) => {
    const wr = formatWeeklyRate(report.weeklyRate);
    return (
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide mb-0.5">{label}</div>
        <div className="text-sm font-semibold text-gray-900 truncate">
          {new Date(report.startedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
        <div className="text-xs text-gray-500" data-testid={`text-compare-rate-${label.toLowerCase()}`}>
          <span className={wr.isFallback ? "italic text-gray-400" : "font-medium"}>{wr.value}</span>
        </div>
      </div>
    );
  };

  const StatRow = ({ label, a, b, format = (v: number) => v.toLocaleString(), higherIsBetter = true }: {
    label: string; a: number; b: number; format?: (v: number) => string; higherIsBetter?: boolean;
  }) => {
    const same = a === b;
    return (
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-1.5 border-b border-gray-100 last:border-0">
        <div className={`text-sm font-semibold tabular-nums text-right ${same ? "text-gray-700" : ""}`} data-testid={`compare-a-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {format(a)}
        </div>
        <div className="text-[10px] text-gray-400 text-center px-2 whitespace-nowrap">
          {label}
          <div className="mt-0.5"><DiffArrow a={a} b={b} higherIsBetter={higherIsBetter} /></div>
        </div>
        <div className={`text-sm font-semibold tabular-nums text-left ${same ? "text-gray-700" : ""}`} data-testid={`compare-b-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {format(b)}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border-2 border-blue-200 rounded-xl p-5 mb-6" data-testid="comparison-view">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-5 w-5 text-blue-600" />
          <h3 className="text-base font-bold text-gray-900">Side-by-side comparison</h3>
        </div>
        <Button size="sm" variant="ghost" onClick={onClear} data-testid="button-clear-comparison">
          <XIcon className="h-4 w-4 mr-1" /> Close
        </Button>
      </div>

      <div className="flex gap-4 mb-4">
        <HeaderCol report={reportA} label="A" />
        <div className="text-gray-300 self-center">vs</div>
        <HeaderCol report={reportB} label="B" />
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <StatRow label="Weekly rate" a={rateA} b={rateB} format={v => v > 0 ? `$${v.toFixed(0)}/wk` : "—"} />
        <StatRow label="Found" a={reportA.totalFound} b={reportB.totalFound} />
        <StatRow label="Passing" a={reportA.totalPassed} b={reportB.totalPassed} />
        <StatRow label="Failing" a={reportA.totalFailed} b={reportB.totalFailed} higherIsBetter={false} />
        <StatRow label="Avg CoC%" a={avgCocA} b={avgCocB} format={v => `${v.toFixed(1)}%`} />
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Passing properties</p>
        {allKeys.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Neither scan has passing properties.</p>
        ) : (
          <div className="space-y-2" data-testid="comparison-properties-list">
            {allKeys.map(key => {
              const a = mapA.get(key);
              const b = mapB.get(key);
              const sample = a || b!;
              const cocA = a ? parseFloat(String(a.cocReturn || "0")) : NaN;
              const cocB = b ? parseFloat(String(b.cocReturn || "0")) : NaN;
              const cocDiff = isFinite(cocA) && isFinite(cocB) ? cocB - cocA : 0;
              const highlightDiff = isFinite(cocA) && isFinite(cocB) && Math.abs(cocDiff) >= 0.5;
              const onlyIn = !a ? "B" : !b ? "A" : null;
              return (
                <div
                  key={key}
                  className={`grid grid-cols-[1fr_auto_1fr] gap-3 items-center p-2 rounded-lg border ${onlyIn ? "border-amber-200 bg-amber-50" : highlightDiff ? "border-blue-200 bg-blue-50/40" : "border-gray-100 bg-white"}`}
                  data-testid={`compare-row-${key}`}
                >
                  <div className="text-right">
                    {a ? (
                      <>
                        <div className="text-xs font-medium text-gray-900 truncate">{a.address || "—"}</div>
                        <div className="text-[11px] text-gray-500 tabular-nums">
                          {fmtPct(a.cocReturn)} CoC · {fmtCurrency(a.monthlyCashFlow)}/mo · {fmtCurrency(a.price)}
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not in A</span>
                    )}
                  </div>
                  <div className="text-center min-w-[90px]">
                    <div className="text-[10px] text-gray-500 truncate max-w-[140px] mx-auto">{sample.address}</div>
                    {isFinite(cocA) && isFinite(cocB) && (
                      <div className="mt-0.5"><DiffArrow a={cocA} b={cocB} /></div>
                    )}
                  </div>
                  <div className="text-left">
                    {b ? (
                      <>
                        <div className="text-xs font-medium text-gray-900 truncate">{b.address || "—"}</div>
                        <div className="text-[11px] text-gray-500 tabular-nums">
                          {fmtPct(b.cocReturn)} CoC · {fmtCurrency(b.monthlyCashFlow)}/mo · {fmtCurrency(b.price)}
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not in B</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportDetail({ id }: { id: number }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [filterPassing, setFilterPassing] = useState<"all" | "passing" | "failing">("all");
  const [sortKey, setSortKey] = useState<SortKey>("cocReturn");
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const [editCriteria, setEditCriteria] = useState<CriteriaFields | null>(null);
  const [criteriaChanged, setCriteriaChanged] = useState(false);
  const [editingWeeklyRate, setEditingWeeklyRate] = useState(false);
  const [weeklyRateInput, setWeeklyRateInput] = useState("");
  const [compareId, setCompareId] = useState<number | null>(null);
  // B21: daily-agent emails / New matches deep-link with ?result=<id>; seed
  // from the URL once (wouter's location omits the query) and flash the card.
  const [focusResultId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("result");
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  });
  const [focusScrolled, setFocusScrolled] = useState(false);

  const detailQuery = useQuery<{ report: Report; results: Result[] }>({
    queryKey: ["/api/app/buy-box/reports", id],
    queryFn: async () => {
      const res = await fetch(`/api/app/buy-box/reports/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    refetchInterval: (query) => {
      const d = query.state.data as { report: Report; results: Result[] } | undefined;
      if (!d || isInProgress(d.report?.status ?? "")) return 5000;
      return false;
    },
  });

  const report = detailQuery.data?.report;
  const allResults = detailQuery.data?.results || [];

  const allReportsQuery = useQuery<Report[]>({
    queryKey: ["/api/app/buy-box/reports"],
  });

  const favoriteIdsQuery = useQuery<{ ids: string[] }>({
    queryKey: ["/api/app/buy-box/favorites/ids"],
  });
  const favoriteIdSet = new Set(favoriteIdsQuery.data?.ids ?? []);

  const favoriteMutation = useMutation({
    mutationFn: async (vars: { result: Result; favorite: boolean }) => {
      const { result, favorite } = vars;
      const listingId = result.listingId || "";
      if (!listingId) throw new Error("Missing listing id");
      if (favorite) {
        const body = {
          listingId,
          sourceReportId: id ?? null,
          address: result.address ?? null,
          city: result.city ?? null,
          state: result.state ?? null,
          zipCode: result.zipCode ?? null,
          price: result.price != null ? String(result.price) : null,
          sqft: result.sqft ?? null,
          lotSize: result.lotSize ?? null,
          garageSpaces: result.garageSpaces ?? null,
          originalBedrooms: result.originalBedrooms ?? null,
          originalBathrooms: result.originalBathrooms != null ? String(result.originalBathrooms) : null,
          convertedBedrooms: result.convertedBedrooms ?? null,
          convertedBathrooms: result.convertedBathrooms ?? null,
          imageUrl: result.imageUrl ?? null,
          propertyType: result.propertyType ?? null,
          listingUrl: result.listingUrl ?? null,
          monthlyCashFlow: result.monthlyCashFlow != null ? String(result.monthlyCashFlow) : null,
          cocReturn: result.cocReturn != null ? String(result.cocReturn) : null,
          totalCashInvested: result.totalCashInvested != null ? String(result.totalCashInvested) : null,
          annualTax: result.annualTax != null ? String(result.annualTax) : null,
          annualTaxIsEstimate: result.annualTaxIsEstimate ?? null,
          monthlyHoa: result.monthlyHoa != null ? String(result.monthlyHoa) : null,
        };
        const res = await apiRequest("POST", "/api/app/buy-box/favorites", body);
        return res.json();
      } else {
        const res = await apiRequest("DELETE", `/api/app/buy-box/favorites/${encodeURIComponent(listingId)}`);
        return res.json();
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/favorites/ids"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/favorites"] });
      toast({ title: vars.favorite ? "Saved to Favorites" : "Removed from Favorites" });
    },
    onError: () => {
      toast({ title: "Could not update favorite", variant: "destructive" });
    },
  });

  const compareCandidates = (allReportsQuery.data || []).filter(
    r => r.id !== id
      && report
      && r.city.toLowerCase() === report.city.toLowerCase()
      && r.state.toLowerCase() === report.state.toLowerCase()
      && !isInProgress(r.status)
      && r.totalFound > 0,
  );

  const compareQuery = useQuery<{ report: Report; results: Result[] }>({
    queryKey: ["/api/app/buy-box/reports", compareId],
    queryFn: async () => {
      const res = await fetch(`/api/app/buy-box/reports/${compareId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    enabled: compareId != null,
  });

  useEffect(() => {
    if (compareId != null && !compareCandidates.some(c => c.id === compareId)) {
      // The currently selected comparison no longer applies (e.g., different city report).
      // Only clear if the candidates list has loaded and report is loaded.
      if (report && allReportsQuery.data) setCompareId(null);
    }
  }, [compareId, compareCandidates, report, allReportsQuery.data]);

  useEffect(() => {
    if (report?.criteriaSnapshot && !editCriteria) {
      setEditCriteria(parseCriteriaSnapshot(report.criteriaSnapshot));
    }
  }, [report?.criteriaSnapshot, editCriteria]);

  const reevaluateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (criteria: Record<string, number | null>) => {
      const res = await apiRequest("POST", `/api/app/buy-box/reevaluate/${id}`, { criteria });
      return res.json();
    },
    onSuccess: (data: { report: Report; results: Result[] }) => {
      queryClient.setQueryData(["/api/app/buy-box/reports", id], data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/reports"] });
      setCriteriaChanged(false);
      toast({ title: "Criteria applied — results updated" });
    },
    onError: () => {
      toast({ title: "Failed to re-evaluate", variant: "destructive" });
    },
  });

  const rescanWithRateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (avgPricePrivate: number) => {
      const res = await apiRequest("POST", `/api/app/buy-box/rescan/${id}`, { avgPricePrivate });
      return res.json() as Promise<{ reportId: number; message: string }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/reports", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/reports"] });
      setEditingWeeklyRate(false);
      toast({ title: data.message });
    },
    onError: () => {
      toast({ title: "Failed to start re-scan", variant: "destructive" });
    },
  });

  const createUnderwritingMutation = useMutation({
    mutationFn: async (result: Result) => {
      // Fall back to parsing the 5-digit ZIP out of the address string when
      // the listing didn't surface a structured zip_code field. This keeps
      // the Underwriting form's ZIP field populated for tax/rate lookups.
      const zipFromAddress = (() => {
        if (result.zipCode) return result.zipCode;
        const m = (result.address || "").match(/\b(\d{5})(?:-\d{4})?\b/);
        return m ? m[1] : "";
      })();
      const garageSpaces = result.garageSpaces || 0;
      // Mirror server/services/zillow-scraper.ts → extractGarageSqft
      // fallback: 200 sqft per garage space when an explicit garage sqft
      // is not available on the listing.
      const garageSqft = garageSpaces > 0 ? garageSpaces * 200 : undefined;
      const propRes = await apiRequest("POST", "/api/app/properties", {
        name: result.address || `${result.city}, ${result.state}`,
        address: result.address || "",
        city: result.city || "",
        state: result.state || "",
        zipCode: zipFromAddress,
        roomCount: result.convertedBedrooms || 0,
        bathroomCount: result.convertedBathrooms || 0,
        purchasePrice: result.price ? String(result.price) : "",
        sqft: result.sqft ? String(result.sqft) : "",
        propertyType: result.propertyType || "",
        zillowUrl: result.listingUrl || "",
        status: "underwriting",
        userId: 0,
        mainImage: result.imageUrl || undefined,
        zillowImport: {
          listingLink: result.listingUrl || undefined,
          sqft: result.sqft || undefined,
          currentBedrooms: result.originalBedrooms || undefined,
          currentBathrooms: result.originalBathrooms ? parseInt(String(result.originalBathrooms)) || undefined : undefined,
          // Seed the already-calculated future bed/bath counts from the Buy
          // Box scan so the host doesn't have to click "Calculate future
          // bedrooms" again on the Underwriting page.
          futureBedrooms: result.convertedBedrooms || undefined,
          futureBathrooms: result.convertedBathrooms || undefined,
          garageSpaces: garageSpaces || undefined,
          garageSqft,
          noGarage: garageSpaces > 0 ? false : undefined,
          homePurchasePrice: result.price ? String(result.price) : undefined,
          zipCode: zipFromAddress || undefined,
        },
      });
      const prop = await propRes.json();
      return prop.id as number;
    },
    onSuccess: (propertyId: number) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      navigate(`/app/underwriting/${propertyId}`);
    },
    onError: () => {
      toast({ title: "Failed to create underwriting record", variant: "destructive" });
    },
  });

  const handleExportPDF = useCallback(async () => {
    if (!report) return;
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");

    const doc = new jsPDF();
    const primaryBlue: [number, number, number] = [0, 82, 204];
    const accentOrange: [number, number, number] = [255, 165, 0];
    const pageWidth = doc.internal.pageSize.getWidth();
    const exportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    doc.setFillColor(...primaryBlue);
    doc.rect(0, 0, pageWidth, 35, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("PadPulse", 14, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Buy Box Report", 14, 26);
    doc.text(exportDate, pageWidth - 14, 18, { align: "right" });
    doc.text(`${report.city}, ${report.state}`, pageWidth - 14, 26, { align: "right" });

    let y = 45;

    doc.setFillColor(...accentOrange);
    doc.rect(14, y, pageWidth - 28, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Summary", 17, y + 5.5);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    y += 12;

    autoTable(doc, {
      startY: y,
      head: [],
      body: [
        ["City", `${report.city}, ${report.state}`],
        ["Status", report.status],
        ["Listings Found", String(report.totalFound)],
        ["Properties Analyzed", String(allResults.length)],
        ["In Buy Box", String(report.totalPassed)],
        ["Outside Buy Box", String(report.totalFailed)],
        ["Weekly Rate Used", formatWeeklyRate(report.weeklyRate).value],
        ["", "Based on shared room rates. Adjust upward for properties with private bathrooms or premium rooms."],
        ["Run Date", new Date(report.startedAt).toLocaleDateString()],
      ],
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 60 }, 1: { halign: "right" } },
      margin: { left: 14, right: 14 },
    });
    y = (doc as typeof doc & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

    const passingResults = allResults.filter(r => r.passesAll);
    const failingResults = allResults.filter(r => !r.passesAll);
    if (passingResults.length > 0) {
      doc.setFillColor(...accentOrange);
      doc.rect(14, y, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("In Buy Box", 17, y + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      y += 12;

      autoTable(doc, {
        startY: y,
        head: [["Address", "Price", "Beds", "Cash Flow", "CoC%"]],
        body: passingResults.map(r => [
          r.address || "—",
          fmtCurrency(r.price),
          String(r.convertedBedrooms || "—"),
          fmtCurrency(r.monthlyCashFlow),
          fmtPct(r.cocReturn),
        ]),
        theme: "striped",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: primaryBlue },
        margin: { left: 14, right: 14 },
      });
      y = (doc as typeof doc & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    }

    if (failingResults.length > 0) {
      doc.setFillColor(220, 38, 38);
      doc.rect(14, y, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`Outside Buy Box (${failingResults.length})`, 17, y + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      y += 12;

      autoTable(doc, {
        startY: y,
        head: [["Address", "Price", "Beds", "Outside Criteria"]],
        body: failingResults.map(r => {
          const failing: string[] = [];
          if (!r.passesCocReturn) failing.push("CoC%");
          if (!r.passesCashFlow) failing.push("Cash Flow");
          if (!r.passesPurchasePrice) failing.push("Price");
          if (!r.passesBedrooms) failing.push("Bedrooms");
          if (!r.passesSqft) failing.push("Sqft");
          return [
            r.address || "—",
            fmtCurrency(r.price),
            String(r.convertedBedrooms || "—"),
            r.errorMessage ? `Error: ${r.errorMessage}` : (failing.length > 0 ? failing.join(", ") : "Unknown"),
          ];
        }),
        theme: "striped",
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [185, 28, 28] as [number, number, number] },
        margin: { left: 14, right: 14 },
      });
    }

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated by PadPulse | ${exportDate}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
    }

    doc.save(`BuyBox_${report.city}_${report.state}_${report.id}.pdf`);
    toast({ title: "PDF exported successfully" });
  }, [report, allResults, toast]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const filteredResults = allResults
    .filter(r => {
      if (filterPassing === "passing" && !r.passesAll) return false;
      if (filterPassing === "failing" && r.passesAll) return false;
      if (search) {
        const s = search.toLowerCase();
        return String(r.address ?? "").toLowerCase().includes(s) || String(r.city ?? "").toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      const va = parseFloat(String(a[sortKey] || "0")) || 0;
      const vb = parseFloat(String(b[sortKey] || "0")) || 0;
      return sortDir === "desc" ? vb - va : va - vb;
    });

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === "desc" ? <ChevronDown className="h-3 w-3 inline" /> : <ChevronUp className="h-3 w-3 inline" />;
  };

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!report) {
    return <div className="text-center py-16 text-gray-400">Report not found.</div>;
  }
  const agentTag = reportAgentTag(report);

  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900" data-testid="text-report-detail-name">{report.runName || `${report.city}, ${report.state}`}</h2>
            <AgentRunBadges report={report} testId="badges-report-detail" />
            <p className="text-sm text-gray-500">{new Date(report.startedAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
            {(() => {
              const { value, isFallback } = formatWeeklyRate(report.weeklyRate);
              const canEdit = !isInProgress(report.status);
              const sharedNote = (
                <p className="text-[11px] text-gray-400 mt-0.5" data-testid="text-weekly-rate-shared-note">
                  Based on shared room rates. Adjust upward for properties with private bathrooms or premium rooms.
                </p>
              );
              const startEditing = () => {
                const current = report.weeklyRate != null && report.weeklyRate !== ""
                  ? String(parseFloat(String(report.weeklyRate)) || "")
                  : "";
                setWeeklyRateInput(current);
                setEditingWeeklyRate(true);
              };
              const submitRescan = () => {
                const num = parseFloat(weeklyRateInput);
                if (!isFinite(num) || num <= 0) {
                  toast({ title: "Enter a weekly rate greater than 0", variant: "destructive" });
                  return;
                }
                rescanWithRateMutation.mutate(num);
              };
              if (editingWeeklyRate && canEdit) {
                return (
                  <>
                  <div className="mt-1 flex items-center gap-1.5" data-testid="weekly-rate-editor">
                    <span className="text-xs text-gray-500">Weekly rate:</span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">$</span>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={weeklyRateInput}
                        onChange={e => setWeeklyRateInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") submitRescan();
                          if (e.key === "Escape") setEditingWeeklyRate(false);
                        }}
                        className="h-7 w-24 pl-5 pr-8 text-xs"
                        placeholder="200"
                        autoFocus
                        data-testid="input-weekly-rate"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">/wk</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={submitRescan}
                      disabled={rescanWithRateMutation.isPending}
                      className="h-7 text-xs"
                      data-testid="button-rescan-with-rate"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${rescanWithRateMutation.isPending ? "animate-spin" : ""}`} />
                      Re-scan with this rate
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingWeeklyRate(false)}
                      disabled={rescanWithRateMutation.isPending}
                      className="h-7 w-7 p-0"
                      data-testid="button-cancel-edit-weekly-rate"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {sharedNote}
                  </>
                );
              }
              return (
                <>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5" data-testid="text-report-weekly-rate">
                    <span>
                      Weekly rate used: <span className={isFallback ? "italic text-gray-400" : "font-medium text-gray-700"}>{value}</span>
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={startEditing}
                        className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                        data-testid="button-edit-weekly-rate"
                      >
                        <Pencil className="h-3 w-3" />
                        change & rescan
                      </button>
                    )}
                  </p>
                  {sharedNote}
                </>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={report.status} />
            {!isInProgress(report.status) && compareCandidates.length > 0 && (
              <Select
                value={compareId != null ? String(compareId) : ""}
                onValueChange={v => setCompareId(v ? parseInt(v, 10) : null)}
              >
                <SelectTrigger className="h-9 text-sm w-48" data-testid="select-compare-report">
                  <div className="flex items-center gap-1.5">
                    <GitCompareArrows className="h-4 w-4 text-blue-600" />
                    <SelectValue placeholder="Compare to..." />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {compareCandidates.map(c => {
                    const wr = formatWeeklyRate(c.weeklyRate);
                    return (
                      <SelectItem key={c.id} value={String(c.id)} data-testid={`option-compare-${c.id}`}>
                        {new Date(c.startedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {wr.value} · {c.totalPassed} passing
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {!isInProgress(report.status) && (
              <Button size="sm" variant="outline" onClick={handleExportPDF} data-testid="button-export-pdf">
                <Download className="h-4 w-4 mr-1.5" />Export PDF
              </Button>
            )}
          </div>
        </div>
        {isStuckRestart(report) ? (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-3" data-testid="banner-stuck-restart">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This scan didn't finish because the server restarted.</p>
              <p className="text-xs text-amber-700 mt-0.5">Run a new scan for the same city — your saved Buy Box criteria will be reused automatically.</p>
            </div>
          </div>
        ) : report.statusNote && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-3">{report.statusNote}</p>
        )}
        <div className="grid grid-cols-4 gap-3 mt-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center relative">
            {report.status === "running" && report.totalFound > 0 && (
              <span
                className="absolute top-1.5 right-1.5 flex h-2 w-2"
                aria-hidden="true"
                data-testid="indicator-live-results-dot"
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
            )}
            <div className="text-2xl font-bold text-gray-900" data-testid="text-total-found">{report.totalFound}</div>
            <div className="text-xs text-gray-500">
              {report.status === "running" && report.totalFound > 0 ? (
                <span className="text-blue-600 font-medium" data-testid="text-found-live-label">Found · live</span>
              ) : (
                "Found"
              )}
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700" data-testid="text-total-analyzed">{allResults.length}</div>
            <div className="text-xs text-gray-500">Analyzed</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700" data-testid="text-total-passed">{report.totalPassed}</div>
            <div className="text-xs text-gray-500">In BB</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600" data-testid="text-total-failed">{report.totalFailed}</div>
            <div className="text-xs text-gray-500">Outside BB</div>
          </div>
        </div>
        {allResults.length > 0 && (
          <p className="text-xs text-gray-400 mt-2" data-testid="text-passed-bedrooms">
            {allResults.filter(r => r.passesBedrooms).length} of {allResults.length} passed bedroom filter
          </p>
        )}
        {report.status === "queued" && (
          <div className="flex items-center gap-2 mt-4 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2" data-testid="text-queued-message">
            <Clock className="h-4 w-4 animate-pulse" />
            <span>Scan is queued and will start shortly…</span>
          </div>
        )}
        {report.status === "running" && (
          <div className="flex items-start gap-2 mt-4 text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2" data-testid="text-running-message">
            <Clock className="h-4 w-4 animate-pulse mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              {report.totalFound > 0 ? (
                <div className="font-medium flex items-center gap-2 flex-wrap" data-testid="text-live-results-headline">
                  <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                  </span>
                  <span>
                    Live results — <span className="tabular-nums">{report.totalFound}</span> found so far
                    <span className="text-blue-500 font-normal"> · refreshing every 5s</span>
                  </span>
                </div>
              ) : (
                <div className="font-medium">Scan is running…</div>
              )}
              <div className="text-blue-600 text-xs mt-0.5">
                {report.totalFound > 0
                  ? "More properties are still being analyzed — the count above will keep growing until the scan finishes."
                  : "Zillow searches typically take 2–5 minutes to complete."}{" "}
                You can leave this page — we'll email you the moment it's done with a link back to the report.
              </div>
            </div>
          </div>
        )}
        {!isInProgress(report.status) && editCriteria && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <Settings2 className="h-3.5 w-3.5" />
                Buy Box Criteria
              </p>
              <div className="flex items-center gap-2">
                {criteriaChanged && (
                  <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Unsaved changes</span>
                )}
                <Button
                  size="sm"
                  onClick={() => reevaluateMutation.mutate(criteriaToPayload(editCriteria))}
                  disabled={reevaluateMutation.isPending || !criteriaChanged}
                  className="text-xs h-7"
                  data-testid="button-reevaluate"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${reevaluateMutation.isPending ? "animate-spin" : ""}`} />
                  {reevaluateMutation.isPending ? "Applying..." : "Re-evaluate"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="criteria-editor">
              {([
                { key: "minCocReturn" as const, label: "Min CoC%", suffix: "%" },
                { key: "minMonthlyCashFlow" as const, label: "Min Cash Flow", prefix: "$" },
                { key: "maxPurchasePrice" as const, label: "Max Price", prefix: "$" },
                { key: "minBedrooms" as const, label: "Min Beds" },
                { key: "maxBedrooms" as const, label: "Max Beds" },
                { key: "minSqft" as const, label: "Min Sqft" },
                { key: "maxSqft" as const, label: "Max Sqft" },
              ]).map(({ key, label, prefix, suffix }) => (
                <div key={key}>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{label}</label>
                  <div className="relative">
                    {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{prefix}</span>}
                    <Input
                      type="number"
                      value={editCriteria[key]}
                      onChange={e => {
                        setEditCriteria(prev => prev ? { ...prev, [key]: e.target.value } : prev);
                        setCriteriaChanged(true);
                      }}
                      className={`h-7 text-xs ${prefix ? "pl-5" : ""} ${suffix ? "pr-6" : ""}`}
                      placeholder="—"
                      data-testid={`input-criteria-${key}`}
                    />
                    {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{suffix}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {report.defaultsSnapshot && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Underwriting Assumptions at Scan Time</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-xs text-gray-700" data-testid="defaults-snapshot">
              {report.defaultsSnapshot.downpaymentPercent != null && (
                <div><span className="text-gray-400">Down%:</span> {String(report.defaultsSnapshot.downpaymentPercent)}%</div>
              )}
              {report.defaultsSnapshot.annualInterestRate != null && (
                <div><span className="text-gray-400">Rate:</span> {String(report.defaultsSnapshot.annualInterestRate)}%</div>
              )}
              {report.defaultsSnapshot.vacancyPercent != null && (
                <div><span className="text-gray-400">Vacancy:</span> {String(report.defaultsSnapshot.vacancyPercent)}%</div>
              )}
              {report.defaultsSnapshot.furnishingCostPerRoom != null && (
                <div><span className="text-gray-400">Furnishing/Rm:</span> ${Number(report.defaultsSnapshot.furnishingCostPerRoom).toLocaleString()}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {compareId != null && (
        compareQuery.isLoading ? (
          <Skeleton className="h-64 w-full rounded-xl mb-6" />
        ) : compareQuery.data?.report ? (
          <ComparisonView
            reportA={report}
            resultsA={allResults}
            reportB={compareQuery.data.report}
            resultsB={compareQuery.data.results}
            onClear={() => setCompareId(null)}
          />
        ) : null
      )}

      {!isInProgress(report.status) && allResults.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search address..."
                className="pl-8 text-sm"
                data-testid="input-search-results"
              />
            </div>
            <Select value={filterPassing} onValueChange={v => setFilterPassing(v as "all" | "passing" | "failing")}>
              <SelectTrigger className="w-44 text-sm" data-testid="select-filter-passing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                <SelectItem value="passing">In Buy Box</SelectItem>
                <SelectItem value="failing">Outside Buy Box</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 text-xs text-gray-500">
              Sort:
              {(["cocReturn", "monthlyCashFlow", "price", "convertedBedrooms"] as SortKey[]).map(k => (
                <button
                  key={k}
                  onClick={() => toggleSort(k)}
                  className={`px-2 py-1 rounded border cursor-pointer transition-colors ${sortKey === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                  data-testid={`button-sort-${k}`}
                >
                  {k === "cocReturn" ? "CoC%" : k === "monthlyCashFlow" ? "Cash Flow" : k === "price" ? "Price" : "Beds"}
                  <SortIcon k={k} />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3" data-testid="results-list" data-tour="buy-box-reports-filters">
            {filteredResults.map(result => (
              <div
                key={result.id}
                ref={result.id === focusResultId && !focusScrolled ? (el) => {
                  if (!el) return;
                  el.scrollIntoView({ block: "center" });
                  setFocusScrolled(true);
                } : undefined}
                className={`bg-white border rounded-xl overflow-hidden ${result.passesAll ? "border-green-200" : "border-gray-200"} ${result.id === focusResultId ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
                data-testid={`result-card-${result.id}`}
                data-focused={result.id === focusResultId ? "true" : undefined}
              >
                <div className="flex gap-4 p-4">
                  {result.imageUrl && (
                    <img
                      src={result.imageUrl}
                      alt={result.address || "Property"}
                      className="h-20 w-28 object-cover rounded-lg shrink-0"
                      onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm truncate" data-testid={`text-address-${result.id}`}>{result.address || "Unknown address"}</p>
                        {agentTag && <BuyBoxAgentTags tags={[agentTag]} testId={`tags-result-${result.id}`} />}
                        <p className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
                          {result.propertyType && <span>{result.propertyType}</span>}
                          {result.sqft != null && (
                            <>
                              {result.propertyType && <span className="text-gray-300">·</span>}
                              <span data-testid={`text-sqft-${result.id}`}>{result.sqft.toLocaleString()} sqft</span>
                            </>
                          )}
                          {result.garageSpaces != null && result.garageSpaces > 0 && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span data-testid={`text-garage-${result.id}`}>{result.garageSpaces}-car garage</span>
                            </>
                          )}
                          {result.lotSize != null && result.lotSize > 0 && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span data-testid={`text-lot-size-${result.id}`}>{result.lotSize.toLocaleString()} sqft lot</span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                        {result.isCornerLot && (
                          <span
                            className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200"
                            title="MLS lot features list this property as a corner lot"
                            data-testid={`badge-corner-lot-${result.id}`}
                          >
                            Corner Lot
                          </span>
                        )}
                        {result.description && /cul[- ]de[- ]sac/i.test(result.description) && (
                          <span
                            className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full border border-blue-200"
                            title="Listing description mentions cul-de-sac"
                            data-testid={`badge-cul-de-sac-${result.id}`}
                          >
                            Cul-de-Sac
                          </span>
                        )}
                        {result.description && /\bend\s+unit\b/i.test(result.description) && (
                          <span
                            className="text-xs font-semibold bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full border border-purple-200"
                            title="Listing description mentions end unit"
                            data-testid={`badge-end-unit-${result.id}`}
                          >
                            End Unit
                          </span>
                        )}
                        {result.passesAll
                          ? <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ In BB</span>
                          : <span className="text-xs font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">✗ Outside BB</span>
                        }
                        {result.listingId && (() => {
                          const isFav = favoriteIdSet.has(result.listingId);
                          return (
                            <button
                              type="button"
                              onClick={() => favoriteMutation.mutate({ result, favorite: !isFav })}
                              disabled={favoriteMutation.isPending}
                              title={isFav ? "Remove from Favorites" : "Save to Favorites"}
                              className={`p-1 rounded-full border transition-colors ${isFav ? "bg-amber-100 border-amber-300 text-amber-600" : "bg-white border-gray-200 text-gray-400 hover:text-amber-500 hover:border-amber-300"}`}
                              data-testid={`button-favorite-${result.id}`}
                            >
                              <Star className={`h-3.5 w-3.5 ${isFav ? "fill-amber-400" : ""}`} />
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <div>
                        <div className="text-xs text-gray-400">Price</div>
                        <div className="font-semibold text-gray-900 text-sm" data-testid={`text-price-${result.id}`}>{fmtCurrency(result.price)}</div>
                        {(() => {
                          const cashInvested = parseFloat(String(result.totalCashInvested || "0"));
                          const beds = result.convertedBedrooms || 0;
                          if (cashInvested > 0 && beds > 0) {
                            return (
                              <div
                                className="text-[10px] text-gray-500 mt-0.5"
                                title="All-in cash invested per converted bedroom (down payment + closing + furnishing + rehab, per your underwriting defaults)"
                                data-testid={`text-cost-per-bed-${result.id}`}
                              >
                                {fmtCurrency(cashInvested / beds)}/bd all-in
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Beds / Baths</div>
                        <div className="font-semibold text-gray-900 text-sm" data-testid={`text-beds-${result.id}`}>{result.convertedBedrooms}bd / {result.convertedBathrooms}ba</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Monthly Cash Flow</div>
                        <div className={`font-semibold text-sm ${parseFloat(String(result.monthlyCashFlow || "0")) >= 0 ? "text-green-700" : "text-red-600"}`} data-testid={`text-cashflow-${result.id}`}>
                          {fmtCurrency(result.monthlyCashFlow)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">CoC Return</div>
                        <div className={`font-semibold text-sm ${parseFloat(String(result.cocReturn || "0")) >= 8 ? "text-green-700" : "text-gray-900"}`} data-testid={`text-coc-${result.id}`}>
                          {fmtPct(result.cocReturn)}
                        </div>
                      </div>
                    </div>
                    {(result.mortgagePI != null || result.annualTax != null || result.monthlyHoa != null) && (
                      <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-50">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">Mortgage P&amp;I</div>
                          <div className="font-medium text-gray-700 text-xs" data-testid={`text-mortgage-${result.id}`}>
                            {result.mortgagePI != null ? `${fmtCurrency(result.mortgagePI)}/mo` : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">Annual Tax</div>
                          <div
                            className="font-medium text-gray-700 text-xs"
                            title={result.annualTaxIsEstimate ? "City-average AI estimate — used because the MLS listing had no annual tax figure." : undefined}
                            data-testid={`text-tax-${result.id}`}
                          >
                            {result.annualTax != null
                              ? (
                                  <>
                                    {fmtCurrency(result.annualTax)}
                                    {result.annualTaxIsEstimate && (
                                      <span className="ml-1 text-[10px] text-amber-600 font-normal" data-testid={`text-tax-estimate-${result.id}`}>(est.)</span>
                                    )}
                                  </>
                                )
                              : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">HOA</div>
                          <div className="font-medium text-gray-700 text-xs" data-testid={`text-hoa-${result.id}`}>
                            {result.monthlyHoa != null && parseFloat(String(result.monthlyHoa)) > 0
                              ? `${fmtCurrency(result.monthlyHoa)}/mo`
                              : "None"}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      <PassBadge passes={result.passesCocReturn} label="CoC%" />
                      <PassBadge passes={result.passesCashFlow} label="Cash Flow" />
                      <PassBadge passes={result.passesPurchasePrice} label="Price" />
                      <PassBadge passes={result.passesBedrooms} label="Bedrooms" />
                      <PassBadge passes={result.passesSqft} label="Sqft" />
                    </div>
                    {!result.passesBedrooms && result.convertedBedrooms != null && (
                      <p className="text-[10px] text-red-500 mt-0.5" data-testid={`text-bedrooms-reason-${result.id}`}>
                        {result.convertedBedrooms} converted bed{result.convertedBedrooms !== 1 ? "s" : ""} — outside required range
                        {report.criteriaSnapshot?.minBedrooms != null ? ` (min ${report.criteriaSnapshot.minBedrooms})` : ""}
                        {report.criteriaSnapshot?.maxBedrooms != null ? ` (max ${report.criteriaSnapshot.maxBedrooms})` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-100 px-4 py-2 flex gap-2 bg-gray-50">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => createUnderwritingMutation.mutate(result)}
                    disabled={createUnderwritingMutation.isPending}
                    data-testid={`button-open-underwriting-${result.id}`}
                  >
                    Open in Underwriting
                  </Button>
                  {result.listingUrl && (
                    <a href={result.listingUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost" className="text-xs" data-testid={`button-listing-${result.id}`}>
                        View Listing <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredResults.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>No properties match your filters.</p>
            </div>
          )}
        </>
      )}

      {!isInProgress(report.status) && allResults.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Home className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-gray-500">No properties were analyzed</p>
          <p className="text-sm mt-1">{report.statusNote || "The scan did not return any results."}</p>
        </div>
      )}
    </div>
  );
}

export default function AppBuyBoxReports() {
  usePageTitle("Buy Box Reports");
  const params = useParams<{ id?: string }>();
  const reportId = params.id ? parseInt(params.id, 10) : null;

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl">
        <ViewOnlyBanner area="underwriting" />
        <div className="flex items-center gap-2 mb-6">
          {reportId ? (
            <div className="flex items-center justify-between w-full">
              <Link href="/app/buy-box/reports">
                <span className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 cursor-pointer" data-testid="link-back-reports">
                  <ArrowLeft className="h-4 w-4" /> Back to Reports
                </span>
              </Link>
              <ExplainerVideoButton pageKey="buy-box-reports" />
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900" data-tour="buy-box-reports-heading">Buy Box Reports</h1>
              </div>
              <ExplainerVideoButton pageKey="buy-box-reports" />
              <GuideButton pageKey="buy-box-reports" />
            </div>
          )}
        </div>
        <BuyBoxSubnav />
        <GuideNudgeBanner pageKey="buy-box-reports" />

        {reportId && !isNaN(reportId) ? (
          <ReportDetail id={reportId} />
        ) : (
          <ReportsList />
        )}
      </div>
    </AppLayout>
  );
}
